/**
 * Dashboard organization filter helpers.
 *
 * Centralizes mapping, sorting, and search filtering for the Partner /
 * Advertiser / Sales Rep MultiSelect so selector, filter drawer, and filter
 * chips stay in sync. The dashboard filter pairs these helpers with
 * `MultiSelect` `virtualized` — see MUI Autocomplete virtualization docs and
 * {@link VirtualizedListbox} for why large org lists need a virtualized listbox.
 *
 * @see {@link https://mui.com/material-ui/react-autocomplete/#virtualization MUI Autocomplete — Virtualization}
 * @see {@link VirtualizedListbox}
 * @see sortRecordsByName
 * @see filterRecordsByKeywords
 */
import {
    filterRecordsByKeywords,
    sortRecordsByName,
} from 'states/resources/organizations/business-logic';

/** Field mapping for MultiSelect `{ label, value }` option shape. */
const ORG_OPTION_FIELDS = { nameField: 'label', idField: 'value' };

/**
 * Maps dashboard `childOrganizations` lookup to sorted MultiSelect options.
 * @param {Object|null|undefined} childOrganizations - id → org lookup from dashboard state
 * @returns {Array<{ label: string, value: string }>}
 */
export function getOrganizationFilterOptions(childOrganizations) {
    if (!childOrganizations) {
        return [];
    }

    const options = Object.values(childOrganizations)
        .filter(Boolean)
        .map(org => ({
            label: org.name,
            value: org.id,
        }));

    return sortRecordsByName(options, ORG_OPTION_FIELDS);
}

/**
 * MUI Autocomplete `filterOptions` callback for organization MultiSelects.
 * Supports accent-insensitive name search and exact match by 24-char org id.
 * @param {Array<{ label: string, value: string }>} options
 * @param {{ inputValue: string }} state - MUI Autocomplete filter state
 * @returns {Array<{ label: string, value: string }>}
 */
export function organizationSelectFilterOptions(options, state) {
    return filterRecordsByKeywords(options, state.inputValue, ORG_OPTION_FIELDS);
}
